import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingRefunds: 0,
    refundFormsFilled: 0,
    approved: 0,
    paid: 0,
    rejected: 0,
    refillRequests: 0,
    rejectAndRefill: 0,
    ordersWithPenalty: 0,
    totalPenaltyAmount: 0,
    totalPaidAmount: 0,
    totalProducts: 0,
    totalMediators: 0,
    totalUsers: 0,
    overdueRefundForms: 0,
    urgentRefundForms: 0,
    closingSoonRefundForms: 0,
  });

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getDashboard();
      const data = response.data?.data || response.data || {};
      setStats({
        totalOrders: data.totalOrders || 0,
        pendingRefunds: data.pendingRefunds || 0,
        refundFormsFilled: data.refundFormsFilled || 0,
        approved: data.approved || 0,
        paid: data.paid || 0,
        rejected: data.rejected || 0,
        refillRequests: data.refillRequests || 0,
        rejectAndRefill: data.rejectAndRefill || 0,
        ordersWithPenalty: data.ordersWithPenalty || 0,
        totalPenaltyAmount: Number(data.totalPenaltyAmount || 0),
        totalPaidAmount: Number(data.totalPaidAmount || 0),
        totalProducts: data.totalProducts || 0,
        totalMediators: data.totalMediators || 0,
        totalUsers: data.totalUsers || 0,
        overdueRefundForms: data.overdueRefundForms || 0,
        urgentRefundForms: data.urgentRefundForms || 0,
        closingSoonRefundForms: data.closingSoonRefundForms || 0,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error(error.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const cards = [
    { label: 'Total Orders', value: stats.totalOrders, path: '/admin/orders', color: '#3b82f6', bg: '#dbeafe', icon: 'orders' },
    { label: 'Pending Refunds', value: stats.pendingRefunds, path: '/admin/orders?status=ORDER_FORM_FILLED,ORDER_FORM_REFILL_REQUEST', color: '#f59e0b', bg: '#fef3c7', icon: 'refund' },
    { label: 'Refund Forms Filled', value: stats.refundFormsFilled, path: '/admin/orders?status=REFUND_FORM_FILLED', color: '#0ea5e9', bg: '#e0f2fe', icon: 'file' },
    { label: 'Approved / Paid', value: stats.approved + stats.paid, path: '/admin/orders?status=APPROVED,PAID,PAID_TO_BUYER,PAID_TO_MEDIATOR', color: '#10b981', bg: '#d1fae5', icon: 'paid' },
    { label: 'Reject & Refill', value: stats.rejectAndRefill, path: '/admin/orders?status=REJECT_AND_REFILL', color: '#ec4899', bg: '#fdf2f8', icon: 'alert-circle' },
    { label: 'Refill Requests', value: stats.refillRequests, path: '/admin/orders?status=ORDER_FORM_REFILL_REQUEST,REFUND_FORM_REFILL_REQUEST,REJECT_AND_REFILL', color: '#db2777', bg: '#fce7f3', icon: 'alert-triangle' },
    { label: 'Penalty Orders', value: stats.ordersWithPenalty, path: '/admin/penalties', color: '#dc2626', bg: '#fee2e2', icon: 'alert-circle' },
    { label: 'Total Penalty ₹', value: `₹${Number(stats.totalPenaltyAmount).toLocaleString()}`, path: '/admin/penalties', color: '#b91c1c', bg: '#fecaca', icon: 'wallet' },
    { label: 'Products', value: stats.totalProducts, path: '/admin/products', color: '#8b5cf6', bg: '#f3e8ff', icon: 'package' },
    { label: 'Mediators', value: stats.totalMediators, path: '/admin/mediators', color: '#2563eb', bg: '#dbeafe', icon: 'usr' },
    { label: 'Users', value: stats.totalUsers, path: '/admin/users', color: '#64748b', bg: '#f1f5f9', icon: 'prof' },
    { label: 'Paid Payout ₹', value: `₹${Number(stats.totalPaidAmount).toLocaleString()}`, path: '/admin/payouts', color: '#059669', bg: '#ecfdf5', icon: 'wallet' },
  ];

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', overflow: 'auto' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#1e293b', margin: '0 0 8px 0' }}>Admin Dashboard</h1>
            <p style={{ fontSize: '16px', color: '#64748b', margin: 0 }}>Live order, refund, penalty, and platform overview</p>
          </div>
          <Button onClick={fetchStats} loading={loading} variant="secondary" icon="refresh">Refresh</Button>
        </div>

        {(stats.closingSoonRefundForms > 0 || stats.urgentRefundForms > 0 || stats.overdueRefundForms > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {stats.closingSoonRefundForms > 0 && (
              <div style={{ padding: '16px 20px', borderRadius: '14px', background: '#fdf2f8', border: '1px solid #f9a8d4', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon name="clock" size={24} color="#db2777" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, color: '#9d174d' }}>{stats.closingSoonRefundForms} refund form(s) closing soon</div>
                  <div style={{ fontSize: '14px', color: '#be185d' }}>Buyers need to fill refund forms before the window closes.</div>
                </div>
                <Button onClick={() => navigate('/admin/orders?refundAlert=CLOSING_SOON')}>View Orders</Button>
              </div>
            )}
            {stats.urgentRefundForms > 0 && (
              <div style={{ padding: '16px 20px', borderRadius: '14px', background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon name="alert-circle" size={24} color="#dc2626" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, color: '#991b1b' }}>{stats.urgentRefundForms} urgent refund(s) / penalty risk</div>
                  <div style={{ fontSize: '14px', color: '#b91c1c' }}>Late refunds may already have or will get a penalty.</div>
                </div>
                <Button onClick={() => navigate('/admin/orders?refundAlert=URGENT')}>View Orders</Button>
              </div>
            )}
            {stats.overdueRefundForms > 0 && (
              <div style={{ padding: '16px 20px', borderRadius: '14px', background: '#fffbeb', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon name="alert-triangle" size={24} color="#d97706" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, color: '#92400e' }}>{stats.overdueRefundForms} overdue refund reminder(s)</div>
                </div>
                <Button onClick={() => navigate('/admin/orders?refundAlert=OVERDUE')}>View Orders</Button>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '28px' }}>
          {cards.map((card) => (
            <div
              key={card.label}
              onClick={() => navigate(card.path)}
              style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '22px',
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>{card.label}</div>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={card.icon} size={20} color={card.color} />
                </div>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#1e293b' }}>{loading ? '...' : card.value}</div>
            </div>
          ))}
        </div>

        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 800 }}>Quick Actions</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <Button onClick={() => navigate('/admin/orders')} icon="sub">Manage Orders</Button>
            <Button onClick={() => navigate('/admin/penalties')} variant="secondary" icon="alert-circle">Penalty Orders</Button>
            <Button onClick={() => navigate('/admin/sheets')} variant="secondary" icon="file">Sheets</Button>
            <Button onClick={() => navigate('/admin/mediators')} variant="secondary" icon="usr">Mediators</Button>
            <Button onClick={() => navigate('/admin/settings')} variant="secondary" icon="settings">Settings</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
